// Typed fetch client for the ahcut Go API.
//
// Responsibilities:
//  - speak the `{ data, error }` envelope (docs/API.md §0); throw a typed `ApiError` on any error;
//  - inject `Authorization: Bearer <accessToken>` from the in-memory token holder;
//  - send `credentials: "include"` so the httpOnly refresh cookie flows to /auth/*;
//  - on a `401`, attempt exactly ONE silent refresh + retry, then give up.
//
// It never touches web storage. The access token comes from `token.ts` (memory only).

import { API_BASE } from "@/lib/config";
import { getAccessToken, setAccessToken } from "@/lib/token";
import type { ApiEnvelope, TokenResponse } from "@/lib/types";

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly nextAllowedAt?: string;
  readonly details?: Record<string, unknown>;

  constructor(
    code: string,
    message: string,
    status: number,
    extra?: { nextAllowedAt?: string; details?: Record<string, unknown> },
  ) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.nextAllowedAt = extra?.nextAllowedAt;
    this.details = extra?.details;
  }
}

// ---- Session-lost notification ----
// api.ts holds no React state; the auth context registers a handler here so a
// FAILED mid-session silent refresh (expired/revoked refresh, or reuse revoking
// the chain) drives the app back to anonymous → RequireAuth redirects to /login.
let authLostHandler: (() => void) | null = null;
export function setAuthLostHandler(handler: (() => void) | null): void {
  authLostHandler = handler;
}

interface RequestOptions {
  method?: string;
  /** JSON body — serialized automatically. */
  body?: unknown;
  /** Extra headers (e.g. Idempotency-Key). */
  headers?: Record<string, string>;
  /** Skip the bearer header (public endpoints like /pricing). */
  anonymous?: boolean;
  /** Skip the auto-refresh-on-401 dance (used by refresh itself). */
  noRetry?: boolean;
  signal?: AbortSignal;
}

function buildHeaders(opts: RequestOptions, hasBody: boolean): Headers {
  const headers = new Headers(opts.headers);
  if (hasBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!opts.anonymous) {
    const token = getAccessToken();
    if (token) headers.set("Authorization", `Bearer ${token}`);
  }
  return headers;
}

async function parseEnvelope<T>(res: Response): Promise<ApiEnvelope<T> | null> {
  if (res.status === 204) return { data: null, error: null };
  const text = await res.text();
  if (!text) return { data: null, error: null };
  try {
    return JSON.parse(text) as ApiEnvelope<T>;
  } catch {
    return null;
  }
}

/**
 * Low-level request. Returns `data` on success, throws `ApiError` on any failure.
 * On a 401 (and unless `noRetry`), tries a single silent refresh then replays the request once.
 */
async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const hasBody = opts.body !== undefined;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      method: opts.method ?? "GET",
      headers: buildHeaders(opts, hasBody),
      body: hasBody ? JSON.stringify(opts.body) : undefined,
      credentials: "include",
      signal: opts.signal,
    });
  } catch (cause) {
    // Network/CORS failure — the backend is unreachable. Surface, never swallow.
    throw new ApiError(
      "network",
      "Tidak dapat terhubung ke server. Periksa koneksi lalu coba lagi.",
      0,
      { details: { cause: String(cause) } },
    );
  }

  if (res.status === 401 && !opts.noRetry && !opts.anonymous) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return request<T>(path, { ...opts, noRetry: true });
    }
    // Unrecoverable session — notify the auth layer so it drops to anonymous
    // and the user is redirected to login instead of seeing per-request errors.
    authLostHandler?.();
  }

  const envelope = await parseEnvelope<T>(res);

  if (!res.ok || envelope?.error) {
    const err = envelope?.error;
    throw new ApiError(
      err?.code ?? "internal",
      err?.message ?? `Permintaan gagal (HTTP ${res.status}).`,
      res.status,
      {
        nextAllowedAt:
          typeof err?.next_allowed_at === "string" ? err.next_allowed_at : undefined,
        details: err as Record<string, unknown> | undefined,
      },
    );
  }

  // Success envelope with `data: null` (e.g. logout) is legal.
  return (envelope?.data as T) ?? (undefined as T);
}

// ---- Silent refresh (single-flight) ----
// The refresh token ROTATES on every use and a failed attempt clears the cookie
// server-side — two overlapping refreshes (checkout poller + user action, or two
// tabs waking together) would race the rotation and can destroy the session.
// In-tab: all concurrent 401s share one promise. Cross-tab: Web Locks serializes
// refreshes on the shared cookie when the API is available.
let refreshInFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  // .catch(() => false) guarantees the shared promise NEVER rejects — a rejection
  // here (e.g. a body-stream error inside parseEnvelope, or a navigator.locks
  // rejection) would otherwise propagate to every awaiter, including auth
  // bootstrap, which does not catch and would leave the app stuck on "loading".
  refreshInFlight ??= refreshExclusive()
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

function refreshExclusive(): Promise<boolean> {
  if (typeof navigator !== "undefined" && "locks" in navigator) {
    return navigator.locks.request("ahcut.refresh", async () => {
      return await refreshOnce();
    }) as unknown as Promise<boolean>;
  }
  return refreshOnce();
}

/**
 * One refresh attempt: POST /auth/refresh with the httpOnly cookie
 * (credentials:"include"). On success, stores the new access token in memory and
 * returns true. Raw fetch (no auto-retry) to avoid recursion. False on any
 * failure — the caller forces re-login.
 */
async function refreshOnce(): Promise<boolean> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
    });
  } catch {
    return false;
  }
  if (!res.ok) return false;
  const envelope = await parseEnvelope<TokenResponse>(res);
  const accessToken = envelope?.data?.access_token;
  if (!accessToken) return false;
  setAccessToken(accessToken);
  return true;
}

// ---- Verb helpers ----
export function apiGet<T>(path: string, opts?: RequestOptions): Promise<T> {
  return request<T>(path, { ...opts, method: "GET" });
}
export function apiPost<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  return request<T>(path, { ...opts, method: "POST", body });
}
export function apiPatch<T>(path: string, body?: unknown, opts?: RequestOptions): Promise<T> {
  return request<T>(path, { ...opts, method: "PATCH", body });
}
export function apiDelete<T>(path: string, opts?: RequestOptions): Promise<T> {
  return request<T>(path, { ...opts, method: "DELETE" });
}
